// ============================================================================
// DataGrid MenuFactory - Shared chrome for dropdowns, popovers, context menus
// ============================================================================
// Centralises the boilerplate that filter popovers, toolbar menus, header
// dropdowns and context menus all re-implemented: BEM scaffold, positioning,
// outside-click close and escape-key close.
//
// Callers own the body content (checkboxes, selects, menu items) and any
// interactive handlers inside it. The factory only owns the chrome.
// ============================================================================

import { adjustMenuPosition } from './menu-positioning.js';
import { toLayoutPx, rectToLayout } from 'shared/utils/zoom.js';

// BEM block + element names per menu type. Keep identical to existing CSS.
const TYPE_CLASSES = {
    menu: {
        root: 'data-grid-toolbar__menu',
        header: 'data-grid-toolbar__menu-header',
        title: 'data-grid-toolbar__menu-title',
        body: 'data-grid-toolbar__menu-body',
        bodyNoPad: 'data-grid-toolbar__menu-body--no-padding',
        footer: 'data-grid-toolbar__menu-footer'
    },
    popover: {
        root: 'data-grid__filter-popover',
        header: 'data-grid__filter-popover-header',
        title: 'data-grid__filter-popover-title',
        body: 'data-grid__filter-popover-body',
        footer: 'data-grid__filter-popover-footer'
    },
    context: { root: 'data-grid__context-menu' }
};

/**
 * Create a menu/popover/context-menu with standard chrome.
 *
 * @param {Object} opts
 * @param {'menu'|'popover'|'context'} [opts.type='menu']
 * @param {string} [opts.title]            Optional header title (menu/popover only)
 * @param {string} [opts.bodyHtml='']      Inner HTML for the body
 * @param {boolean} [opts.bodyPadding=true] When false, adds the no-padding body modifier
 * @param {Array<{label:string,variant?:'primary'|'secondary',id?:string,onClick:Function}>} [opts.footer]
 *                                         Footer buttons (menu/popover only)
 * @param {HTMLElement|{x:number,y:number}} opts.anchor  Trigger element (menu/popover) or {x,y} (context)
 * @param {'bottom-start'|'point'} [opts.placement='bottom-start']
 * @param {string} [opts.extraClass]       Extra class name(s) added to the root
 * @param {boolean} [opts.baseClass=true]  When false, the type's base class (e.g.
 *   data-grid__context-menu) is omitted so a caller supplying fully self-contained
 *   chrome via extraClass isn't subject to the data-grid stylesheet's cascade.
 * @param {HTMLElement} [opts.trigger]     Element the outside-click handler ignores
 *   (so re-clicking it doesn't auto-close; the caller owns the toggle). Defaults to
 *   the anchor element for non-context menus.
 * @param {Function} [opts.onClose]        Called after the menu closes (either path)
 * @param {HTMLElement} [opts.rootEl=document.body] Attach point
 * @returns {{el:HTMLElement, show:Function, hide:Function, destroy:Function}}
 */
export function createMenu({
    type = 'menu',
    title,
    bodyHtml = '',
    bodyPadding = true,
    footer,
    anchor,
    placement = 'bottom-start',
    extraClass,
    baseClass = true,
    trigger,
    onClose,
    rootEl = document.body
} = {}) {
    const cls = TYPE_CLASSES[type];
    if (!cls) throw new Error(`createMenu: unknown type "${type}"`);

    const isContext = (type === 'context') || (placement === 'point');

    // --- Build DOM ----------------------------------------------------------
    const el = document.createElement('div');
    // baseClass:false lets a caller own the chrome entirely via extraClass (it
    // must then style position/z-index/etc itself); fall back to the base class
    // so it is never left class-less.
    el.className = baseClass
        ? (extraClass ? `${cls.root} ${extraClass}` : cls.root)
        : (extraClass || cls.root);

    if (type === 'context') {
        // Context menus are a flat list - caller-supplied bodyHtml IS the content.
        el.innerHTML = bodyHtml;
    } else {
        const parts = [];
        if (title) {
            parts.push(`<div class="${cls.header}"><span class="${cls.title}">${title}</span></div>`);
        }
        const bodyCls = bodyPadding ? cls.body : `${cls.body} ${cls.bodyNoPad}`;
        parts.push(`<div class="${bodyCls}">${bodyHtml}</div>`);

        if (Array.isArray(footer) && footer.length > 0) {
            const buttonsHtml = footer.map((btn, i) => {
                const variant = btn.variant === 'primary' ? 'btn--primary' : 'btn--secondary';
                const idAttr = btn.id ? ` id="${btn.id}"` : '';
                return `<button type="button" class="btn ${variant} btn--sm" data-footer-index="${i}"${idAttr}>${btn.label}</button>`;
            }).join('');
            parts.push(`<div class="${cls.footer}">${buttonsHtml}</div>`);
        }
        el.innerHTML = parts.join('');
    }

    // --- Listener bookkeeping -----------------------------------------------
    let isOpen = false;
    let outsideClickHandler = null;
    let escapeHandler = null;
    let footerClickHandler = null;

    const tearDown = () => {
        if (outsideClickHandler) {
            document.removeEventListener('click', outsideClickHandler, true);
            outsideClickHandler = null;
        }
        if (escapeHandler) {
            document.removeEventListener('keydown', escapeHandler);
            escapeHandler = null;
        }
        if (footerClickHandler) {
            el.removeEventListener('click', footerClickHandler);
            footerClickHandler = null;
        }
    };

    const close = () => {
        const wasOpen = isOpen;
        isOpen = false;
        tearDown();
        if (el.parentNode) el.parentNode.removeChild(el);
        if (wasOpen && onClose) onClose();
    };

    // --- Positioning --------------------------------------------------------
    const position = () => {
        if (isContext) {
            // {x,y} arrive in visual pixels (clientX/Y or a getBoundingClientRect
            // corner); convert to layout space since the menu is a root-zoomed
            // body overlay. No-op at zoom 1.
            const { x, y } = anchor || { x: 0, y: 0 };
            el.style.left = `${toLayoutPx(x)}px`;
            el.style.top = `${toLayoutPx(y)}px`;
            adjustMenuPosition(el);
            return;
        }
        // rectVisual stays visual for adjustMenuPosition (it converts triggerRect
        // internally); rect is layout space for the direct left/top writes.
        const rectVisual = anchor.getBoundingClientRect();
        const rect = rectToLayout(rectVisual);
        el.style.position = 'fixed';
        const gap = type === 'popover' ? 4 : 5;
        el.style.top = `${rect.bottom + gap}px`;
        el.style.left = `${rect.left}px`;
        adjustMenuPosition(el, { triggerRect: rectVisual });
    };

    // --- Show ---------------------------------------------------------------
    const show = () => {
        if (isOpen) return;
        isOpen = true;
        rootEl.appendChild(el);
        position();

        // Footer button click delegation (menu/popover only)
        if (!isContext && Array.isArray(footer) && footer.length > 0) {
            footerClickHandler = (e) => {
                const btnEl = e.target.closest('[data-footer-index]');
                if (!btnEl || !el.contains(btnEl)) return;
                const idx = parseInt(btnEl.dataset.footerIndex, 10);
                const def = footer[idx];
                if (def && typeof def.onClick === 'function') def.onClick(e);
            };
            el.addEventListener('click', footerClickHandler);
        }

        // Outside click - capture phase so it beats content handlers.
        // Deferred one tick so the click that opened us doesn't immediately close us.
        // An explicit `trigger` is ignored by the outside-click (the caller toggles);
        // otherwise non-context menus default to ignoring their anchor element.
        const triggerEl = trigger || (isContext ? null : anchor);
        setTimeout(() => {
            if (!isOpen) return;
            outsideClickHandler = (e) => {
                if (el.contains(e.target)) return;
                if (triggerEl && (triggerEl === e.target || triggerEl.contains(e.target))) return;
                close();
            };
            document.addEventListener('click', outsideClickHandler, true);
        }, 0);

        // Escape to close
        escapeHandler = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', escapeHandler);
    };

    return { el, show, hide: close, destroy: close };
}
